"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const REPLIES = ["Node_rt_reply1", "Node_rt_reply2", "Node_rt_reply3"];
const CHIPS = ["Node_rt_chip1", "Node_rt_chip2", "Node_rt_chip3"];

// Deux personnes connectées au même salon. Le panneau "Vous" et le panneau
// "Alice" affichent la MÊME conversation : un message envoyé apparait au même
// instant des deux côtés. C'est ça, le temps réel.
const PEERS = [
    { owner: "you", nameKey: "Node_rt_you", initial: "V", color: "bg-green-500" },
    { owner: "alice", nameKey: "Node_rt_alice", initial: "A", color: "bg-violet-500" },
];

export default function NodeRealtime() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const idRef = useRef(2);
    const replyRef = useRef(0);
    const timersRef = useRef([]);

    const [messages, setMessages] = useState([
        { id: 1, author: "alice", textKey: "Node_rt_seed_alice" },
        { id: 2, author: "you", textKey: "Node_rt_seed_you" },
    ]);
    const [draft, setDraft] = useState("");
    const [relay, setRelay] = useState(0);
    const [aliceTyping, setAliceTyping] = useState(false);

    const youRef = useRef(null);
    const aliceRef = useRef(null);

    useEffect(() => {
        const timers = timersRef.current;
        return () => timers.forEach((id) => clearTimeout(id));
    }, []);

    useEffect(() => {
        for (const el of [youRef.current, aliceRef.current]) {
            if (el) el.scrollTop = el.scrollHeight;
        }
    }, [messages, aliceTyping]);

    function send(value) {
        const text = value.trim();
        if (!text) return;
        const id = (idRef.current += 1);
        setMessages((m) => [...m, { id, author: "you", text }]);
        setDraft("");
        setRelay((r) => r + 1);

        if (reduce) return;
        setAliceTyping(true);
        const typingTimer = setTimeout(() => {
            setAliceTyping(false);
            const replyId = (idRef.current += 1);
            const key = REPLIES[replyRef.current % REPLIES.length];
            replyRef.current += 1;
            setMessages((m) => [...m, { id: replyId, author: "alice", textKey: key }]);
            setRelay((r) => r + 1);
        }, 1500);
        timersRef.current.push(typingTimer);
    }

    function onSubmit(e) {
        e.preventDefault();
        send(draft);
    }

    function Bubble({ msg, owner }) {
        const mine = msg.author === owner;
        const text = msg.text ?? t(msg.textKey);
        return (
            <motion.div
                layout={!reduce}
                initial={reduce ? false : { opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: "spring", stiffness: 380, damping: 26 }}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
            >
                <span
                    className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-[12.5px] leading-snug ${
                        mine
                            ? "bg-green-500 text-white rounded-br-sm"
                            : "bg-white/10 text-gray-100 rounded-bl-sm"
                    }`}
                >
                    {text}
                </span>
            </motion.div>
        );
    }

    function Panel({ peer, scrollRef }) {
        const other = peer.owner === "you" ? PEERS[1] : PEERS[0];
        return (
            <div className="min-w-0 rounded-2xl bg-bgDark-900/60 ring-1 ring-white/10 overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-white/[0.03]">
                    <span
                        className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold text-white ${peer.color}`}
                    >
                        {peer.initial}
                    </span>
                    <span className="text-[12px] font-semibold text-gray-200">
                        {t(peer.nameKey)}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-green-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                        {t("Node_rt_connected")}
                    </span>
                </div>
                <div
                    ref={scrollRef}
                    className="h-48 sm:h-52 overflow-y-auto px-3 py-3 flex flex-col gap-2"
                >
                    <AnimatePresence initial={false}>
                        {messages.map((msg) => (
                            <Bubble key={msg.id} msg={msg} owner={peer.owner} />
                        ))}
                    </AnimatePresence>
                    {aliceTyping && (
                        <div
                            className={`flex ${
                                peer.owner === "alice"
                                    ? "justify-end"
                                    : "justify-start"
                            }`}
                        >
                            <span className="inline-flex items-center gap-1 rounded-2xl bg-white/10 px-3 py-2">
                                {[0, 1, 2].map((i) => (
                                    <motion.span
                                        key={i}
                                        className="w-1.5 h-1.5 rounded-full bg-gray-300"
                                        animate={{ opacity: [0.3, 1, 0.3] }}
                                        transition={{
                                            duration: 0.9,
                                            repeat: Infinity,
                                            delay: i * 0.15,
                                        }}
                                    />
                                ))}
                            </span>
                        </div>
                    )}
                </div>
                <div className="px-3 py-1.5 border-t border-white/10 text-[10px] text-gray-500">
                    {t("Node_rt_seen_by", { name: t(other.nameKey) })}
                </div>
            </div>
        );
    }

    return (
        <section id="nodejs-realtime" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Node_rt_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Node_rt_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (teinte Node) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-green-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:socket-io"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        chat.js
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-green-300">
                            {t("Node_rt_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7">
                    {/* Serveur central */}
                    <div className="flex justify-center mb-5">
                        <div className="relative inline-flex items-center gap-2.5 rounded-2xl bg-white/[0.05] ring-1 ring-white/10 px-4 py-2.5">
                            <span className="relative flex items-center justify-center w-8 h-8">
                                <motion.span
                                    key={relay}
                                    initial={
                                        reduce ? false : { scale: 0.6, opacity: 0.6 }
                                    }
                                    animate={{ scale: 1.8, opacity: 0 }}
                                    transition={{ duration: 0.9, ease: "easeOut" }}
                                    className="absolute inline-flex w-8 h-8 rounded-full bg-green-400/40"
                                />
                                <span className="relative flex items-center justify-center w-8 h-8 rounded-xl bg-green-500/20 ring-1 ring-green-400/30">
                                    <Icon
                                        icon="logos:nodejs-icon"
                                        className="w-4 h-4"
                                        aria-hidden="true"
                                    />
                                </span>
                            </span>
                            <div className="leading-tight">
                                <div className="text-[12.5px] font-semibold text-white">
                                    {t("Node_rt_server")}
                                </div>
                                <div className="inline-flex items-center gap-1 text-[10.5px] text-green-300">
                                    <SolarIcon
                                        name="users-group-rounded-bold"
                                        className="w-3 h-3"
                                    />
                                    {t("Node_rt_online")}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Les deux clients connectés */}
                    <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                        <Panel peer={PEERS[0]} scrollRef={youRef} />
                        <Panel peer={PEERS[1]} scrollRef={aliceRef} />
                    </div>

                    {/* Zone de saisie (vous écrivez) */}
                    <div className="mt-5">
                        <form onSubmit={onSubmit} className="flex items-center gap-2">
                            <input
                                type="text"
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder={t("Node_rt_placeholder")}
                                maxLength={120}
                                className="flex-1 min-w-0 rounded-full bg-white/[0.06] ring-1 ring-white/10 focus:ring-green-400/50 outline-hidden px-4 py-2.5 text-[13px] text-white placeholder:text-gray-500"
                            />
                            <button
                                type="submit"
                                aria-label={t("Node_rt_send")}
                                className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-green-500 hover:bg-green-400 px-4 py-2.5 text-[13px] font-semibold text-bgDark-900 transition active:scale-95"
                            >
                                <SolarIcon name="plain-2-bold" className="w-4 h-4" />
                                {t("Node_rt_send")}
                            </button>
                        </form>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            {CHIPS.map((key) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => send(t(key))}
                                    className="rounded-full bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-3 py-1.5 text-[12.5px] text-gray-200 transition active:scale-95"
                                >
                                    {t(key)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Node_rt_caption")}
            </p>
        </section>
    );
}
